<script setup>
import { useMasterAuthStore } from "@/composables/store/master/useAuthStore"
import { useSuccessStore } from "@/composables/store/useSuccessStore"
import Logo from "@images/landing/logo.png"
import { VForm } from "vuetify/components/VForm"

definePage({
    meta: {
        layout: "blank",
        unauthenticatedOnly: true,
    },
})

const router = useRouter()
const route = useRoute()

const authStore = useMasterAuthStore()
const successStore = useSuccessStore()

const refVForm = ref(null)

const isPasswordVisible = ref(false)
const isConfirmPasswordVisible = ref(false)

const errors = ref({
    email: undefined,
    password: undefined,
    password_confirmation: undefined,
    general: undefined,
})

const environment = import.meta.env.VITE_ENV === "development" ? "Development " : ""

const tokenFromQuery = computed(() => (route.query?.token ?? "").toString())
const emailFromQuery = computed(() => (route.query?.email ?? "").toString())

const form = ref({
    email: "",
    token: "",
    password: "",
    password_confirmation: "",
})

const emailLocked = computed(() => !!emailFromQuery.value)
const isSubmitting = computed(() => authStore?.isResetPasswordLoading ?? false)

const passwordMatchValidator = (value) => {
    if (!value) return "This field is required"
    if (value !== form.value.password) return "Passwords do not match"
    return true
}

onMounted(() => {
    successStore.clearSuccessMessage()

    // Pull from reset link
    form.value.email = emailFromQuery.value || ""
    form.value.token = tokenFromQuery.value || ""

    // fallback if your backend uses different param name
    if (!form.value.token && route.query?.reset_token) {
        form.value.token = route.query.reset_token.toString()
    }
})

onUnmounted(() => {
    successStore.clearSuccessMessage()
})

const onSubmit = async () => {
    const { valid } = (await refVForm.value?.validate()) || { valid: false }
    if (!valid) return

    errors.value = {
        email: undefined,
        password: undefined,
        password_confirmation: undefined,
        general: undefined,
    }

    // Token must exist from the link (hidden)
    if (!form.value.token) {
        errors.value.general = "Invalid or expired reset link. Please request a new reset email."
        return
    }

    try {
        await authStore.resetPassword({
            token: form.value.token,
            email: form.value.email,
            password: form.value.password,
            password_confirmation: form.value.password_confirmation,
        })

        successStore.setSuccessMessage("Password reset successful. Please login with your new password.")
        await router.push({ name: "master-authentication-login" })
    } catch (error) {
        const data = error?.response?.data
        const validationErrors = data?.errors || null

        if (validationErrors) {
            errors.value.email = validationErrors.email?.[0]
            errors.value.password = validationErrors.password?.[0]
            errors.value.password_confirmation = validationErrors.password_confirmation?.[0]
            errors.value.general = data?.message
            return
        }

        errors.value.general =
            data?.message || data?.error || error?.message || "Reset password failed. Please try again."
    }
}
</script>

<template>
    <VRow no-gutters class="auth-wrapper bg-surface">
        <!-- Left panel -->
        <VCol md="7" class="d-none d-md-flex auth-logo-section animate-fade-in">
            <div class="auth-logo-content">
                <VImg :src="Logo" class="auth-logo" />
                <p class="auth-logo-text text-brand">MARBLE SOLUTION</p>
            </div>
        </VCol>

        <!-- Right panel -->
        <VCol cols="12" md="5" class="auth-form-section">
            <VCard flat class="auth-form-card animate-slide-up">
                <VCardText class="auth-form-content">
                    <div class="mb-4">
                        <h2 class="text-h2 mb-1">{{ environment }}MARBLE ADMIN</h2>
                        <p class="mb-0">Set a new password for your master account.</p>
                    </div>

                    <VAlert v-if="errors.general" type="error" variant="tonal" class="mb-4" density="comfortable">
                        {{ errors.general }}
                    </VAlert>

                    <VForm ref="refVForm" @submit.prevent="onSubmit">
                        <VRow>
                            <!-- Email (readonly if comes from link) -->
                            <VCol cols="12">
                                <AppTextField v-model="form.email" :disabled="isSubmitting" :readonly="emailLocked"
                                    label="Email" placeholder="master@email.com" type="email"
                                    :rules="[requiredValidator, emailValidator]" :error-messages="errors.email" />
                            </VCol>

                            <!-- New Password -->
                            <VCol cols="12">
                                <AppTextField v-model="form.password" :disabled="isSubmitting" label="New Password"
                                    placeholder="············" :type="isPasswordVisible ? 'text' : 'password'"
                                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                                    @click:append-inner="isPasswordVisible = !isPasswordVisible"
                                    :rules="[requiredValidator]" :error-messages="errors.password" />
                            </VCol>

                            <!-- Confirm Password -->
                            <VCol cols="12">
                                <AppTextField v-model="form.password_confirmation" :disabled="isSubmitting"
                                    label="Confirm Password" placeholder="············"
                                    :type="isConfirmPasswordVisible ? 'text' : 'password'"
                                    :append-inner-icon="isConfirmPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                                    @click:append-inner="isConfirmPasswordVisible = !isConfirmPasswordVisible"
                                    :rules="[passwordMatchValidator]" :error-messages="errors.password_confirmation" />
                            </VCol>

                            <VCol cols="12">
                                <VBtn color="primary" :loading="isSubmitting" :disabled="isSubmitting" block
                                    type="submit">
                                    Reset Password
                                </VBtn>

                                <div class="d-flex justify-center mt-4">
                                    <RouterLink class="text-primary" :to="{ name: 'master-authentication-login' }">
                                        <VIcon icon="tabler-chevron-left" class="me-1" />
                                        Back to login
                                    </RouterLink>
                                </div>
                            </VCol>
                        </VRow>
                    </VForm>
                </VCardText>
            </VCard>
        </VCol>
    </VRow>
</template>

<style lang="scss" scoped>
.auth-logo-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    margin: 0;
    background: #232323;
    color: white;
    min-block-size: 100vh;
}

.auth-logo {
    display: block;
    inline-size: 100%;
    max-block-size: 200px;
    max-inline-size: 280px;
    object-fit: contain;
}

.auth-logo-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    gap: 0.75rem;
}

.auth-logo-text {
    margin: 0;
    font-size: clamp(1.5rem, 2vw + 1rem, 2.5rem);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.auth-form-section {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(var(--v-theme-surface));
    min-block-size: 100vh;
}

.auth-form-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background-color: rgb(var(--v-theme-surface));
    inline-size: 100%;
    overflow-y: auto;
}

.auth-form-content {
    align-items: center;
    inline-size: 100%;
    margin-block: 0;
    margin-inline: auto;
    max-inline-size: 500px;

    @media (max-width: 600px) {
        inline-size: 95%;
        padding-block: 1rem;
        padding-inline: 0;
    }
}

@keyframes fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slide-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fade-in {
    animation: fade-in 0.8s ease-in-out;
}

.animate-slide-up {
    animation: slide-up 0.6s ease-out;
}

.v-btn {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 15%);
        transform: translateY(-2px);
    }
}

.v-text-field {
    transition: transform 0.2s ease;

    &:focus-within {
        transform: scale(1.01);
    }
}

.text-primary {
    transition: color 0.2s ease;

    &:hover {
        opacity: 0.85;
    }
}
</style>
